import { useState, useMemo } from 'react';
import { Task } from './useTasks';

export interface TaskFilterState {
  search: string;
  statuses: string[];
  priorities: string[];
  projectIds: string[];
  tags: string[];
  dueDateFrom: Date | null;
  dueDateTo: Date | null;
  assignedToContactIds: string[];
  showAICreatedOnly: boolean;
  showWithEmailsOnly: boolean;
  showOverdueOnly: boolean;
}

const initialFilterState: TaskFilterState = {
  search: '',
  statuses: [],
  priorities: [],
  projectIds: [],
  tags: [],
  dueDateFrom: null,
  dueDateTo: null,
  assignedToContactIds: [],
  showAICreatedOnly: false,
  showWithEmailsOnly: false,
  showOverdueOnly: false,
};

export function useTaskFilters() {
  const [filters, setFilters] = useState<TaskFilterState>(initialFilterState);

  const updateFilters = (updates: Partial<TaskFilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  };

  const clearFilters = () => {
    setFilters(initialFilterState);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.priorities.length > 0) count++;
    if (filters.projectIds.length > 0) count++;
    if (filters.tags.length > 0) count++;
    if (filters.dueDateFrom || filters.dueDateTo) count++;
    if (filters.assignedToContactIds.length > 0) count++;
    if (filters.showAICreatedOnly) count++;
    if (filters.showWithEmailsOnly) count++;
    if (filters.showOverdueOnly) count++;
    return count;
  }, [filters]);

  const applyFilters = (tasks: Task[]): Task[] => {
    return tasks.filter(task => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesTitle = task.title.toLowerCase().includes(searchLower);
        const matchesDescription = task.description?.toLowerCase().includes(searchLower);
        if (!matchesTitle && !matchesDescription) return false;
      }

      // Status filter
      if (filters.statuses.length > 0 && !filters.statuses.includes(task.status)) {
        return false;
      }

      // Priority filter
      if (filters.priorities.length > 0 && !filters.priorities.includes(task.priority)) {
        return false;
      }

      // Project filter
      if (filters.projectIds.length > 0) {
        const hasNoProject = filters.projectIds.includes('no_project');
        const taskProjectId = task.project_id;
        
        if (hasNoProject && !taskProjectId) {
          // Task has no project and we're filtering for no project
        } else if (!taskProjectId || !filters.projectIds.includes(taskProjectId)) {
          return false;
        }
      }

      // Tags filter (match any tag)
      if (filters.tags.length > 0) {
        const taskTags = task.tags || [];
        const hasMatchingTag = filters.tags.some(tag => taskTags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      // Due date range filter
      if (filters.dueDateFrom || filters.dueDateTo) {
        if (!task.due_date) return false;
        const taskDueDate = new Date(task.due_date);
        
        if (filters.dueDateFrom) {
          const fromDate = new Date(filters.dueDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (taskDueDate < fromDate) return false;
        }
        
        if (filters.dueDateTo) {
          const toDate = new Date(filters.dueDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (taskDueDate > toDate) return false;
        }
      }

      // Assigned to filter
      if (filters.assignedToContactIds.length > 0) {
        const hasUnassigned = filters.assignedToContactIds.includes('unassigned');
        const taskAssignedTo = task.assigned_to_contact_id;
        
        if (hasUnassigned && !taskAssignedTo) {
          // Task is unassigned and we're filtering for unassigned
        } else if (!taskAssignedTo || !filters.assignedToContactIds.includes(taskAssignedTo)) {
          return false;
        }
      }

      // AI-created only toggle
      if (filters.showAICreatedOnly && !task.created_by_ai) {
        return false;
      }

      // With emails only toggle
      if (filters.showWithEmailsOnly && (task.linked_emails_count || 0) === 0) {
        return false;
      }

      // Overdue only toggle
      if (filters.showOverdueOnly) {
        if (!task.due_date || task.status === 'done') return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate >= today) return false;
      }

      return true;
    });
  };

  return {
    filters,
    updateFilters,
    clearFilters,
    activeFilterCount,
    applyFilters,
  };
}
